import classNames from "classnames";
import { useTranslation } from "next-i18next";
import Link from "next/link";
import { useRouter } from "next/router";
import { FC, useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import ButtonLoadMore from "~/components/ButtonLoadMore";
import Card from "~/components/Card";
import CardSkeleton from "~/components/CardSkeleton";
import { Container } from "~/components/Container";
import EmptyData from "~/components/EmptyData";
import { resourceNameByType } from "~/constants/data";
import {
  Category,
  Resource,
  ResourceApi,
  ResourceTypes,
} from "~/services/resource";
import Trans from "~/types/translationKeys";
import styles from "./ResourceListSection.module.scss";

interface ResourceListSectionProps {
  id: string;
  variant?: "primary" | "secondary";
  title?: string;
  type: ResourceTypes;
  isLoadingMore?: boolean;
  categories?: Category[];
  totalItemRenderInRow?: number;
  pageSize?: number;
  className?: string;
  containerClassName?: string;
}

interface ResourceListState {
  page: number;
  data: Resource[] | null;
  page_count: number;
  total: number;
}

const ResourceListSection: FC<ResourceListSectionProps> = ({
  variant = "primary",
  title,
  type,
  id,
  isLoadingMore = false,
  categories,
  totalItemRenderInRow = 4,
  pageSize = 10,
  className,
  containerClassName,
}) => {
  const router = useRouter();
  const category: string | string[] | undefined = router.query?.category;
  const [selectedCategoryDebounce] = useDebounce(category, 500);
  const [resource, setResource] = useState<ResourceListState>({
    page: 1,
    data: null,
    total: 0,
    page_count: 1,
  });
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (resource.page !== 1) {
      setLoading(true);
    }

    ResourceApi.getResourceByType({
      page: resource.page,
      page_size: pageSize,
      type,
      categories: selectedCategoryDebounce || undefined,
    }).then((res) => {
      setLoading(false);

      setResource((prevResource) => ({
        ...prevResource,
        data:
          prevResource.page === 1
            ? res.data
            : prevResource.data?.concat(res.data) || [],
        page_count: res.page_count,
        total: res.total,
      }));
    });
  }, [resource.page, pageSize, type, selectedCategoryDebounce]);

  const renderList = () => {
    if (resource.data === null) {
      return (
        <div
          className={classNames(styles.list, {
            "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4":
              totalItemRenderInRow === 4,
            "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3":
              totalItemRenderInRow === 3,
          })}
        >
          {Array(6)
            .fill({})
            .map((_, index) => (
              <CardSkeleton index={index} key={index} variant={variant} />
            ))}
        </div>
      );
    }

    const handleToggleCategory = (categorySlug: string) => {
      const newCategory = category === categorySlug ? undefined : categorySlug;
      const isCurrentResourcePage = router.pathname === "/resources/[type]";

      const query = { ...router.query, category: newCategory };

      if (!newCategory) delete query.category;

      router.push(
        {
          pathname: isCurrentResourcePage ? null : `/resources/${type}`,
          query,
        },
        undefined,
        {
          scroll: !isCurrentResourcePage,
          shallow: true,
        }
      );
    };

    const renderCategories = () => {
      if (categories?.length) {
        return (
          <div className="flex flex-wrap gap-x-3 gap-y-2">
            {categories?.map((c) => (
              <div
                className={classNames(
                  "py-2 px-5 rounded-[64px] border border-[#A3A9B3] cursor-pointer text-sm text-[#36393F] transition-all",
                  {
                    "bg-[#364DE7] text-white": category === c.slug,
                  }
                )}
                key={c.id}
                onClick={() => handleToggleCategory(c.slug)}
              >
                {c.name}
              </div>
            ))}
          </div>
        );
      }
    };

    return (
      <>
        <div
          className={classNames("flex flex-col gap-y-7 md:gap-y-6", className)}
        >
          {renderCategories()}

          <div
            className={classNames(styles.list, {
              "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4":
                totalItemRenderInRow === 4,
              "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3":
                totalItemRenderInRow === 3,
            })}
          >
            {resource.data?.map((resource) => (
              <Card
                name={resource.name}
                href={`/resources/${type}/${resource.slug}`}
                thumbnail={resource.thumbnail?.url}
                key={resource.id}
                showIconPlay={type === ResourceTypes.PRODUCT_VIDEOS}
                time={resource.createdAt}
                variant={variant}
                tags={resource.categories?.map((c) => c.name)}
              />
            ))}
          </div>
        </div>

        {isLoadingMore && (
          <ButtonLoadMore
            loading={loading}
            page={resource.page}
            page_count={resource.page_count}
            handleLoadMore={() =>
              setResource((prevResource) => ({
                ...prevResource,
                page: prevResource.page + 1,
              }))
            }
          />
        )}
      </>
    );
  };

  return (
    <section className={styles.resource_list_section} id={id}>
      <Container className={classNames(styles.container, containerClassName)}>
        {title && (
          <div className="flex items-center justify-between">
            <h2 className={styles.title}>{title}</h2>
            {resource.total > 0 ? (
              <Link href={`/resources/${type}`} className={styles.see_all}>
                {t(Trans.see_all)}
              </Link>
            ) : null}
          </div>
        )}

        {renderList()}

        {!resource.data?.length ? (
          <EmptyData
            text={`${t(Trans.no_content_available_for)} “${t(
              resourceNameByType[type]
            )}”`}
          />
        ) : null}
      </Container>
    </section>
  );
};

export default ResourceListSection;
